<template>
  <div class="document-preview">
    <div class="document-toolbar" aria-label="PDF controls">
      <button type="button" aria-label="Zoom out" :disabled="zoom <= 1" @click="zoom = Math.max(1, zoom - 0.25)">−</button>
      <button type="button" class="fit-button" @click="zoom = 1">{{ Math.round(zoom * 100) }}%</button>
      <button type="button" aria-label="Zoom in" :disabled="zoom >= 2.5" @click="zoom += 0.25">+</button>
      <span class="toolbar-spacer"></span>
      <a :href="src" target="_blank" rel="noopener noreferrer">Open PDF</a>
    </div>
    <div ref="viewport" class="document-viewport" tabindex="0" aria-label="Original Right to Work document">
      <p v-if="loading" class="preview-message" role="status">Loading PDF…</p>
      <div v-if="error" class="preview-message" role="alert">
        <p>{{ error }}</p>
        <button type="button" @click="loadDocument">Retry</button>
      </div>
      <div v-show="!loading && !error" class="document-pages">
        <canvas
          v-for="pageNumber in pageCount"
          :key="pageNumber"
          :ref="(element) => setCanvas(pageNumber, element)"
          role="img"
          :aria-label="`Document page ${pageNumber} of ${pageCount}`"
        ></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min?url';

const props = defineProps({ src: { type: String, required: true } });
const viewport = ref(null);
const canvases = new Map();
const loading = ref(true);
const error = ref('');
const pageCount = ref(0);
const zoom = ref(1);
const viewportWidth = ref(0);
let documentToken = 0;
let renderToken = 0;
let loadingTask = null;
let pdf = null;
let renderTask = null;
let resizeObserver = null;

const setCanvas = (pageNumber, element) => {
  if (element) canvases.set(pageNumber, element);
  else canvases.delete(pageNumber);
};

const renderPages = async () => {
  const token = ++renderToken;
  const previousRender = renderTask;
  previousRender?.cancel();
  if (previousRender) await previousRender.promise.catch(() => {});
  if (token !== renderToken || !pdf) return;
  const currentPdf = pdf;
  try {
    const width = Math.max(240, viewportWidth.value - 32);
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    for (let pageNumber = 1; pageNumber <= currentPdf.numPages; pageNumber++) {
      if (token !== renderToken) return;
      const page = await currentPdf.getPage(pageNumber);
      const surface = canvases.get(pageNumber);
      if (token !== renderToken || !surface) return;
      const natural = page.getViewport({ scale: 1 });
      const pageViewport = page.getViewport({ scale: width / natural.width * zoom.value });
      surface.width = Math.floor(pageViewport.width * outputScale);
      surface.height = Math.floor(pageViewport.height * outputScale);
      surface.style.width = `${pageViewport.width}px`;
      surface.style.height = `${pageViewport.height}px`;
      renderTask = page.render({
        canvasContext: surface.getContext('2d'),
        viewport: pageViewport,
        transform: [outputScale, 0, 0, outputScale, 0, 0],
      });
      await renderTask.promise;
    }
    if (token === renderToken) error.value = '';
  } catch (err) {
    if (token === renderToken && err?.name !== 'RenderingCancelledException') {
      error.value = 'Unable to display this PDF. Try again or open the original.';
    }
  }
};

const loadDocument = async () => {
  const token = ++documentToken;
  renderToken++;
  renderTask?.cancel();
  void loadingTask?.destroy().catch(() => {});
  loadingTask = null;
  pdf = null;
  loading.value = true;
  error.value = '';
  pageCount.value = 0;
  zoom.value = 1;
  try {
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist/legacy/build/pdf');
    if (token !== documentToken) return;
    GlobalWorkerOptions.workerSrc = pdfWorker;
    loadingTask = getDocument({ url: props.src });
    const loaded = await loadingTask.promise;
    if (token !== documentToken) return;
    pdf = loaded;
    pageCount.value = loaded.numPages;
    await nextTick();
    viewportWidth.value = viewport.value?.clientWidth || 600;
    await renderPages();
  } catch {
    if (token === documentToken) error.value = 'Unable to load the PDF preview.';
  } finally {
    if (token === documentToken) loading.value = false;
  }
};

watch(() => props.src, loadDocument, { immediate: true });
watch([zoom, viewportWidth], () => { if (!loading.value) void renderPages(); });
onMounted(() => {
  resizeObserver = new ResizeObserver(([entry]) => { viewportWidth.value = entry.contentRect.width; });
  resizeObserver.observe(viewport.value);
});
onBeforeUnmount(() => {
  documentToken++;
  renderToken++;
  resizeObserver?.disconnect();
  renderTask?.cancel();
  void loadingTask?.destroy().catch(() => {});
  canvases.clear();
});
</script>

<style scoped>
.document-preview { display: flex; flex-direction: column; min-width: 0; min-height: 0; height: 100%; }
.document-toolbar { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid var(--border-color, #dfe3ea); font-size: 0.8rem; }
.document-toolbar button, .preview-message button { min-height: 36px; min-width: 36px; border: 1px solid var(--border-color, #dfe3ea); border-radius: 6px; background: #fff; color: inherit; font: inherit; cursor: pointer; touch-action: manipulation; }
.document-toolbar button:disabled { opacity: 0.4; cursor: default; }
.document-toolbar a { color: var(--noc-blue, #0a61c9); padding: 8px 4px; white-space: nowrap; }
.toolbar-spacer { flex: 1; }
.document-toolbar .fit-button { min-width: 52px; }
.document-viewport { flex: 1; min-height: 0; overflow: auto; overscroll-behavior: contain; scrollbar-gutter: stable; padding: 16px; background: #eef0f3; }
.document-pages { display: flex; flex-direction: column; align-items: center; gap: 16px; width: max-content; min-width: 100%; }
canvas { display: block; flex-shrink: 0; background: white; box-shadow: 0 2px 8px #26334d14; }
.preview-message { text-align: center; color: var(--text-color-secondary, #656b7b); font-size: 0.86rem; padding: 20px; }
button:focus-visible, a:focus-visible, .document-viewport:focus-visible { outline: 2px solid var(--noc-blue, #0a61c9); outline-offset: 2px; }
</style>
